import React, { useCallback, useEffect, useState } from "react";

import ApiKey from "models/ApiKey";
import ProjectService from "api/Project/ProjectService";
import ApiKeysTable from "../ApiKeysTable";
import TextField from "components/TextField";
import ComboBoxField from "components/ComboBoxField";
import ComboBoxItem from "components/ComboBox/ComboBoxItem";
import HighlightBox from "components/HighlightBox";
import FinishDialog from "components/FinishDialog";

import styles from "./ApiKeysTab.module.scss";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

interface ApiKeysTabProps {
  proj_id: string;
}

const ApiKeysTab = ({ proj_id }: ApiKeysTabProps) => {
  const [keyData, setKeyData] = useState<ApiKey[]>([]);

  const [name, setName] = useState<string>("");
  const [validityPeriod, setValidityPeriod] = useState<ComboBoxItem<number>>();
  const [plainKey, setPlainKey] = useState<string>();

  const [isWarningDialogOpen, setIsWarningDialogOpen] =
    useState<boolean>(false);
  const [keyToDelete, setKeyToDelete] = useState<number>();

  const validityPeriodOptions: ComboBoxItem<number>[] = [
    { id: 0, value: 7, label: "1 Week" },
    { id: 1, value: 31, label: "1 Month" },
    { id: 2, value: 365, label: "1 Year" },
  ];

  const getKeyData = useCallback(async () => {
    if (proj_id === undefined) {
      return;
    }

    try {
      const response = await ProjectService.getProjectKeys(proj_id);
      setKeyData(response.keys);
    } catch (err) {
      // TODO: Catch error
    }
  }, [proj_id]);

  useEffect(() => {
    getKeyData();
  }, [getKeyData]);

  const onButtonDelete = (key_id: number) => {
    setKeyToDelete(key_id);
    setIsWarningDialogOpen(true);
  };

  const onDialogDelete = async () => {
    if (keyToDelete === undefined) {
      return;
    }

    try {
      await ProjectService.deleteProjectKey(proj_id, keyToDelete);
      getKeyData();
    } catch (err) {
      // handle error
    }
  };

  const onCreate = async () => {
    if (validityPeriod === undefined || name.length == 0) {
      return;
    }

    try {
      const response = await ProjectService.newProjectKey(
        proj_id,
        name,
        validityPeriod.value,
      );
      setPlainKey(response.plain);
      getKeyData(); // refresh data
    } catch (err) {
      // handle error
    }
  };

  return (
    <div className={styles.content}>
      <div className={styles.keysSection}>
        <div className={styles.header}>
          <div className={styles.title}>API Keys</div>
          <div className={styles.subtitle}>
            Keys for this project are shown here
          </div>
        </div>
        {keyData.length == 0 && (
          <div className={styles.emptyKeys}>No Keys....</div>
        )}
        {keyData.length > 0 && (
          <div className="ag-theme-quartz" style={{ height: 300 }}>
            <ApiKeysTable data={keyData} onDelete={onButtonDelete} />
          </div>
        )}
      </div>

      <div className={styles.createKeySection}>
        <div className={styles.title}>Create New Key</div>
        {plainKey && (
          <HighlightBox
            title="Key is shown once only, please save this securely"
            text={`Key: ${plainKey}`}
            onClose={() => setPlainKey(undefined)}
          />
        )}
        <div className={styles.container}>
          <div className={styles.form}>
            <TextField label="Name" value={name} setValue={setName} />
            <ComboBoxField
              label="Validity Period"
              options={validityPeriodOptions}
              selected={validityPeriod}
              onChange={setValidityPeriod}
            />
          </div>
          <div className={styles.createButton} onClick={onCreate}>
            Create
          </div>
        </div>
      </div>

      {/* Warning Dialog for deleting API Keys */}
      <FinishDialog
        warning
        isOpen={isWarningDialogOpen}
        setIsOpen={setIsWarningDialogOpen}
        title="Warning"
        message="All requests made with the selected key will no longer be valid!"
        onConfirm={onDialogDelete}
      />
    </div>
  );
};

export default ApiKeysTab;
